import React, { Component } from "react";
import withStyles, { WithStyles } from "@material-ui/core/styles/withStyles";
import {
  Button,
  Typography,
  Theme,
  createStyles,
  CircularProgress,
  Paper,
  Chip,
  Divider,
} from "@material-ui/core";
import {
  Payment,
  ChevronRight,
  CheckCircleOutline,
  HourglassEmpty,
  LinkOff,
} from "@material-ui/icons";
import paymentApi from "../../api/payment";
import Config from "../../config";
import { showSnackbar } from "../SharedComponents/Notifier";
import { profileFeatureCardSurface } from "./profileFeatureCardSurface";

export interface StripeConnectReadiness {
  ready: boolean;
  hasAccount: boolean;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  detailsSubmitted: boolean;
  accountId: string | null;
  message?: string;
}

interface Props extends WithStyles {
  /** Hide when viewing another user's profile — Connect status is always for the logged-in user */
  disabled?: boolean;
}

interface State {
  loading: boolean;
  status: StripeConnectReadiness | null;
  error: string | null;
}

class StripeProfileSection extends Component<Props, State> {
  state: State = {
    loading: false,
    status: null,
    error: null,
  };

  componentDidMount() {
    if (this.props.disabled) {
      return;
    }
    this.fetchStatus();
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.disabled && !this.props.disabled) this.fetchStatus();
    if (!prevProps.disabled && this.props.disabled) this.setState({ loading: false, status: null, error: null });
  }

  fetchStatus = async () => {
    if (this.props.disabled) return;
    this.setState({ loading: true, error: null });
    try {
      const { data } = await paymentApi.getStripeConnectStatus();
      this.setState({ status: data as StripeConnectReadiness, loading: false });
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { error?: string } }; message?: string };
      const msg = ax?.response?.data?.error || ax?.message || "Could not load Stripe status";
      this.setState({ error: msg, loading: false });
    }
  };

  openStripeConnect = async () => {
    const { status } = this.state;
    const useDashboard = status?.ready && status.hasAccount;
    try {
      if (useDashboard) {
        const { data } = await paymentApi.createStripeConnectDashboardLink();
        if (data?.url) {
          window.location.href = data.url as string;
          return;
        }
        showSnackbar("Stripe did not return a dashboard URL.", 6000, "error");
        return;
      }
      const href = window.location.href.split("#")[0];
      const { data } = await paymentApi.createStripeConnectAccountLink(href, href);
      if (data?.url) {
        window.location.href = data.url as string;
      } else {
        showSnackbar("Stripe did not return an onboarding URL.", 6000, "error");
      }
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { error?: string } }; message?: string };
      const detail = ax?.response?.data?.error || ax?.message;
      showSnackbar(
        detail ? `Stripe: ${detail}` : "Could not open Stripe. Try again later.",
        8000,
        "error",
      );
    }
  };

  statusSummary(status: StripeConnectReadiness): string {
    if (status.ready) return "Ready to receive payments";
    if (!status.hasAccount) return "Not connected";
    const parts: string[] = [];
    if (!status.detailsSubmitted) parts.push("onboarding incomplete");
    else {
      if (!status.chargesEnabled) parts.push("charges pending");
      if (!status.payoutsEnabled) parts.push("payouts pending");
    }
    return parts.length ? parts.join(" · ") : "Setup in progress";
  }

  render() {
    const { classes, disabled } = this.props;
    const { loading, status, error } = this.state;

    if (disabled) return null;

    const ready = status?.ready;
    const hasAccount = status?.hasAccount;

    return (
      <Paper className={classes.root} elevation={0}>
        <div className={classes.headerRow}>
          <div className={classes.iconBadge} aria-hidden>
            <Payment className={classes.iconBadgeSvg} />
          </div>
          <div className={classes.headerText}>
            <Typography variant="subtitle1" component="h2" className={classes.title}>
              Entry fees and payouts
            </Typography>
            <Typography variant="body2" color="textSecondary" className={classes.subtitle}>
              Connect Stripe to take payments for paid games. Schala applies a small platform fee per transaction.
            </Typography>
          </div>
        </div>

        {Config.DEV_BYPASS_STRIPE_CONNECT_READINESS ? (
          <div className={classes.devBanner} role="status">
            <Typography variant="caption" className={classes.devBannerText}>
              Development: Connect readiness is bypassed in this build. Disable{" "}
              <code className={classes.code}>REACT_APP_DEV_BYPASS_STRIPE_CONNECT_READINESS</code> to test real
              onboarding.
            </Typography>
          </div>
        ) : null}

        <Divider className={classes.divider} />

        <div className={classes.statusBlock}>
          {loading ? (
            <div className={classes.loadingRow}>
              <CircularProgress size={22} thickness={4} className={classes.spinner} />
              <Typography variant="body2" color="textSecondary">
                Checking payment setup…
              </Typography>
            </div>
          ) : error ? (
            <div className={classes.errorRow}>
              <Typography variant="body2" color="error" className={classes.errorText}>
                {error}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={this.fetchStatus}
                className={classes.retryBtn}
              >
                Retry
              </Button>
            </div>
          ) : status ? (
            <div className={classes.statusPanel}>
              <div className={classes.statusMain}>
                {ready ? (
                  <Chip
                    size="small"
                    icon={<CheckCircleOutline className={classes.chipIcon} />}
                    label="Ready"
                    className={`${classes.chip} ${classes.chipReady}`}
                  />
                ) : hasAccount ? (
                  <Chip
                    size="small"
                    icon={<HourglassEmpty className={classes.chipIcon} />}
                    label="Action needed"
                    className={`${classes.chip} ${classes.chipPending}`}
                  />
                ) : (
                  <Chip
                    size="small"
                    icon={<LinkOff className={classes.chipIcon} />}
                    label="Not connected"
                    className={`${classes.chip} ${classes.chipIdle}`}
                  />
                )}
                <Typography
                  component="p"
                  className={classes.summary}
                  title={status.message || ""}
                >
                  {this.statusSummary(status)}
                </Typography>
              </div>
              {!ready && status.message ? (
                <Typography component="p" className={classes.hint}>
                  {status.message}
                </Typography>
              ) : null}
            </div>
          ) : (
            <Typography variant="body2" color="textSecondary">
              No status yet.
            </Typography>
          )}
        </div>

        <Button
          fullWidth
          variant="contained"
          disableElevation
          className={classes.cta}
          onClick={this.openStripeConnect}
          disabled={loading}
        >
          <span className={classes.ctaInner}>
            <span className={classes.ctaLabel}>
              {status?.ready ? "Open Stripe dashboard" : "Set up payments"}
            </span>
            <ChevronRight className={classes.ctaChevron} aria-hidden />
          </span>
        </Button>
      </Paper>
    );
  }
}

const styles = (theme: Theme) =>
  createStyles({
    root: {
      ...profileFeatureCardSurface(theme),
      padding: theme.spacing(1.75, 1.75, 1.75),
      marginBottom: theme.spacing(1.5),
      borderRadius: 10,
      border: `1px solid ${theme.palette.divider}`,
      [theme.breakpoints.up("sm")]: {
        padding: theme.spacing(2.5, 2.5, 2),
        marginBottom: theme.spacing(2),
        borderRadius: 12,
      },
    },
    headerRow: {
      display: "flex",
      alignItems: "flex-start",
      gap: theme.spacing(1.5),
      marginBottom: theme.spacing(1.5),
    },
    iconBadge: {
      width: 44,
      height: 44,
      borderRadius: 10,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      backgroundColor:
        theme.palette.type === "dark" ? "rgba(148, 163, 184, 0.12)" : "rgba(15, 23, 42, 0.06)",
      border: `1px solid ${theme.palette.type === "dark" ? "rgba(148, 163, 184, 0.2)" : "rgba(15, 23, 42, 0.08)"}`,
    },
    iconBadgeSvg: {
      fontSize: 22,
      color: theme.palette.type === "dark" ? theme.palette.grey[300] : theme.palette.grey[800],
    },
    headerText: {
      minWidth: 0,
      flex: 1,
    },
    title: {
      fontWeight: 600,
      letterSpacing: "-0.01em",
      lineHeight: 1.3,
    },
    subtitle: {
      marginTop: theme.spacing(0.5),
      lineHeight: 1.45,
      maxWidth: "42rem",
    },
    devBanner: {
      marginBottom: theme.spacing(1.5),
      padding: theme.spacing(1, 1.25),
      borderRadius: 8,
      backgroundColor: theme.palette.type === "dark" ? "rgba(234, 179, 8, 0.1)" : "rgba(234, 179, 8, 0.12)",
      border: `1px solid ${theme.palette.type === "dark" ? "rgba(234, 179, 8, 0.25)" : "rgba(202, 138, 4, 0.35)"}`,
    },
    devBannerText: {
      lineHeight: 1.45,
      color: theme.palette.text.secondary,
    },
    code: {
      fontFamily: "monospace",
      fontSize: "0.7rem",
      padding: "1px 4px",
      borderRadius: 4,
      backgroundColor: theme.palette.type === "dark" ? "rgba(0,0,0,0.25)" : "rgba(0,0,0,0.06)",
    },
    divider: {
      marginBottom: theme.spacing(1.5),
    },
    statusBlock: {
      marginBottom: theme.spacing(2),
    },
    loadingRow: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1.5),
    },
    spinner: {
      color: theme.palette.grey[600],
    },
    errorRow: {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      gap: theme.spacing(1),
      [theme.breakpoints.up("sm")]: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
      },
    },
    errorText: {
      flex: 1,
      minWidth: 0,
    },
    retryBtn: {
      textTransform: "none",
      borderRadius: 8,
    },
    statusPanel: {
      padding: theme.spacing(1.25, 1.5),
      borderRadius: 8,
      backgroundColor:
        theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.04)" : "rgba(255, 255, 255, 0.55)",
      border: `1px solid ${theme.palette.type === "dark" ? "rgba(148, 163, 184, 0.15)" : "rgba(15, 23, 42, 0.06)"}`,
    },
    statusMain: {
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: theme.spacing(1, 1.25),
    },
    chip: {
      fontWeight: 600,
      letterSpacing: "0.01em",
      height: 24,
      borderRadius: 6,
      flexShrink: 0,
    },
    chipIcon: {
      fontSize: "0.9375rem !important",
    },
    chipReady: {
      backgroundColor: theme.palette.type === "dark" ? "rgba(16, 185, 129, 0.16)" : "rgba(16, 185, 129, 0.12)",
      color: theme.palette.type === "dark" ? "#6ee7b7" : "#047857",
      border: `1px solid ${theme.palette.type === "dark" ? "rgba(16, 185, 129, 0.35)" : "rgba(5, 150, 105, 0.35)"}`,
    },
    chipPending: {
      backgroundColor: theme.palette.type === "dark" ? "rgba(245, 158, 11, 0.12)" : "rgba(245, 158, 11, 0.14)",
      color: theme.palette.type === "dark" ? "#fcd34d" : "#b45309",
      border: `1px solid ${theme.palette.type === "dark" ? "rgba(245, 158, 11, 0.35)" : "rgba(217, 119, 6, 0.35)"}`,
    },
    chipIdle: {
      backgroundColor: theme.palette.type === "dark" ? "rgba(148, 163, 184, 0.1)" : "rgba(100, 116, 139, 0.1)",
      color: theme.palette.text.secondary,
      border: `1px solid ${theme.palette.divider}`,
    },
    summary: {
      margin: 0,
      flex: "1 1 12rem",
      minWidth: 0,
      fontSize: "0.8125rem",
      fontWeight: 500,
      lineHeight: 1.35,
      color: theme.palette.type === "dark" ? theme.palette.grey[400] : theme.palette.grey[700],
    },
    hint: {
      margin: theme.spacing(1, 0, 0),
      fontSize: "0.75rem",
      lineHeight: 1.4,
      color: theme.palette.text.secondary,
    },
    cta: {
      textTransform: "none",
      borderRadius: 10,
      padding: theme.spacing(1.15, 1.75),
      fontWeight: 600,
      letterSpacing: "0.01em",
      minHeight: 48,
      backgroundColor: "#dde0ea",
      color: "#1e293b",
      boxShadow: "none",
      transition: "background-color 0.2s cubic-bezier(0.16, 1, 0.3, 1), transform 0.15s ease",
      [theme.breakpoints.up("sm")]: {
        borderRadius: 12,
        padding: theme.spacing(1.25, 2),
      },
      "&:hover": {
        backgroundColor: "#d3d7e3",
        boxShadow: "none",
      },
      "&.Mui-disabled": {
        backgroundColor: "#e8eaef",
        color: "rgba(30, 41, 59, 0.45)",
      },
      "&:active": {
        transform: "scale(0.98)",
        backgroundColor: "#c9cedc",
      },
    },
    ctaInner: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      width: "100%",
      gap: theme.spacing(1),
    },
    ctaLabel: {
      flex: 1,
      textAlign: "left",
    },
    ctaChevron: {
      fontSize: 20,
      color: "#475569",
      flexShrink: 0,
    },
  });

export default withStyles(styles)(StripeProfileSection);
